import { useState, type ReactNode } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { getEdicion, edicionActiva, type Edicion } from '@/config/ediciones';
import { PlanningCobertura } from '@/components/PlanningCobertura';
import { HERRAMIENTAS, FASES, enlaceHerramienta, type Herramienta } from '@/tools/registro';

/**
 * Panel de equipo — ventana interna con el planning de cobertura y los atajos a
 * las herramientas de un campeonato. Es "por edición": la URL /panel/<id> elige
 * el campeonato (ver src/config/ediciones.ts). Para un campeonato nuevo NO se
 * toca esta vista, solo se añade una entrada a EDICIONES.
 *
 * El catálogo de herramientas vive en src/tools/registro.ts (patrón DLC).
 */

/** Rango de fechas legible. Si empieza y acaba el mismo mes: «24–26 de julio de
 *  2026»; si cruza de mes: «30 de julio – 2 de agosto de 2026». */
function rangoFechas(e: Edicion): string {
  const a = new Date(e.fechaInicio + 'T00:00:00');
  const b = new Date(e.fechaFin + 'T00:00:00');
  const mes = (d: Date) => d.toLocaleDateString('es-ES', { month: 'long' });
  const y = b.getFullYear();
  if (a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()) {
    return `${a.getDate()}–${b.getDate()} de ${mes(b)} de ${y}`;
  }
  return `${a.getDate()} de ${mes(a)} – ${b.getDate()} de ${mes(b)} de ${y}`;
}

/** Estado temporal de la edición respecto a hoy. */
function estadoEdicion(e: Edicion): { label: string; enCurso: boolean } {
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const ini = new Date(e.fechaInicio + 'T00:00:00');
  const fin = new Date(e.fechaFin + 'T23:59:59');
  if (hoy.getTime() < ini.getTime()) {
    const dias = Math.ceil((ini.getTime() - hoy.getTime()) / 86_400_000);
    return { label: dias === 1 ? 'Empieza mañana' : `Faltan ${dias} días`, enCurso: false };
  }
  if (hoy.getTime() > fin.getTime()) return { label: 'Finalizado', enCurso: false };
  return { label: 'En curso', enCurso: true };
}

/** Sección plegable del panel (Planning, Previa, Durante, Después): flecha
 *  grande junto al nombre para abrir/cerrar; de inicio TODAS cerradas (Riki
 *  2026-07-21). El contenido no se monta hasta abrir (el planning no carga
 *  su repo si no se mira). */
function SeccionColapsable({ titulo, extra, children, fija = false }: { titulo: string; extra?: ReactNode; children: ReactNode; fija?: boolean }) {
  const [abierta, setAbierta] = useState(fija);
  const abiertaReal = fija || abierta;
  const tituloStyle: React.CSSProperties = {
    fontSize: 14,
    color: 'var(--ld-text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    fontWeight: 700,
  };
  // `fija`: siempre abierta y sin flecha (p. ej. "Planning de cobertura", que
  // Riki quiere ver siempre desplegado, 2026-07-22).
  if (fija) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <span style={tituloStyle}>{titulo}</span>
          {extra}
        </div>
        {children}
      </div>
    );
  }
  return (
    <div>
      <button
        type="button"
        onClick={() => setAbierta((v) => !v)}
        aria-expanded={abiertaReal}
        style={{
          display: 'flex', alignItems: 'center', gap: 12, width: '100%',
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          textAlign: 'left', color: 'inherit', marginBottom: abiertaReal ? 12 : 0,
        }}
      >
        <svg
          width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="var(--ld-accent)"
          strokeWidth={3.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden
          style={{ transform: abiertaReal ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s ease', flexShrink: 0 }}
        >
          <polyline points="9 5 16 12 9 19" />
        </svg>
        <span style={tituloStyle}>{titulo}</span>
        {extra}
      </button>
      {abiertaReal ? children : null}
    </div>
  );
}

function TarjetaHerramienta({ h, campCode }: { h: Herramienta; campCode?: string }) {
  const contenido = (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 22, lineHeight: 1 }} aria-hidden>
          {h.icono}
        </span>
        <span style={{ fontWeight: 600, fontSize: 15 }}>{h.titulo}</span>
        {h.estado === 'proximo' ? (
          <span className="ld-pill ld-pill--muted" style={{ marginLeft: 'auto', fontSize: 10 }}>
            Próximamente
          </span>
        ) : (
          <span className="faint mono" style={{ marginLeft: 'auto', fontSize: 12 }}>
            →
          </span>
        )}
      </div>
      <p className="muted" style={{ margin: '8px 0 0', fontSize: 13, lineHeight: 1.4 }}>
        {h.descripcion}
      </p>
    </>
  );

  const enlaceBase = h.estado === 'listo' ? enlaceHerramienta(h) : null;
  // Capa de contexto (Riki 2026-07-22): si el panel conoce el código del
  // campeonato (edicion.rfeaCode), lo añadimos a la URL (?camp=<code>) para que
  // la herramienta abra YA cargada con ese campeonato, no en su estado base.
  const enlace = enlaceBase && campCode
    ? `${enlaceBase}${enlaceBase.includes('?') ? '&' : '?'}camp=${encodeURIComponent(campCode)}`
    : enlaceBase;
  if (enlace) {
    return (
      <Link
        to={enlace}
        className="ld-card"
        style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}
      >
        {contenido}
      </Link>
    );
  }
  return (
    <div className="ld-card" style={{ opacity: 0.55, cursor: 'default' }} aria-disabled>
      {contenido}
    </div>
  );
}

export function Panel() {
  const { edicion: edId } = useParams();
  const ed = edId ? getEdicion(edId) : edicionActiva();

  // Slug desconocido en la URL → vuelve a la edición activa.
  if (!ed) return <Navigate to={`/panel/${edicionActiva().id}`} replace />;

  const est = estadoEdicion(ed);

  return (
    <div className="ld-stack ld-stack--lg">
      <div className="ld-card">
        <p className="muted mono" style={{ margin: 0, fontSize: 11, letterSpacing: '0.06em' }}>
          PANEL DE EQUIPO · COBERTURA
        </p>
        <div className="ld-spread" style={{ marginTop: 4, alignItems: 'flex-start' }}>
          <div>
            <h2 className="ld-card__title" style={{ margin: '2px 0 0' }}>
              {ed.titulo}
            </h2>
            <p className="muted" style={{ margin: '4px 0 0' }}>
              {ed.sede}
            </p>
            <p className="faint mono" style={{ margin: '2px 0 0', fontSize: 12 }}>
              {rangoFechas(ed)}
            </p>
          </div>
          <span className={est.enCurso ? 'ld-pill ld-pill--record' : 'ld-pill ld-pill--muted'}>
            {est.label}
          </span>
        </div>
      </div>

      {/* Planning arriba, por encima de las herramientas. El título lo pone
          la sección plegable; el componente esconde el suyo (sinCabecera)
          para no duplicarlo. */}
      <SeccionColapsable
        titulo="Planning de cobertura"
        fija
        extra={
          <span
            className="ld-pill ld-pill--muted"
            style={{ marginLeft: 'auto', fontSize: 10 }}
            title="Guardado solo en este navegador hasta conectar Firestore"
          >
            Provisional · aún no compartido
          </span>
        }
      >
        <PlanningCobertura edicion={ed} sinCabecera />
      </SeccionColapsable>

      {FASES.map((f) => {
        const items = HERRAMIENTAS.filter((h) => h.fase === f.id);
        if (!items.length) return null;
        return (
          <SeccionColapsable key={f.id} titulo={f.label}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                gap: 12,
              }}
            >
              {items.map((h) => (
                <TarjetaHerramienta key={h.id} h={h} campCode={ed.rfeaCode || undefined} />
              ))}
            </div>
          </SeccionColapsable>
        );
      })}
    </div>
  );
}
